/**
 * Tells the user a newer build is available, and lets them take it when they
 * are ready.
 *
 * Deliberately not a toast: a toast auto-dismisses after five seconds, and
 * someone who steps away from a long note would never see it. This sits quietly
 * at the bottom until it is acted on or dismissed.
 *
 * Deliberately not an automatic reload either — that discards unsaved work to
 * fix a problem the user may not have. Reloading is their call.
 */
import { useState } from 'react';
import { RefreshCw, X } from 'lucide-react';
import useDeploymentVersion from '@/hooks/useDeploymentVersion';
import { useAppTranslation } from '@/contexts/TranslationContext';

export default function UpdateAvailableBanner() {
  const { updateAvailable, reload } = useDeploymentVersion();
  const { t } = useAppTranslation();
  const [dismissed, setDismissed] = useState(false);

  if (!updateAvailable || dismissed) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 rounded-lg border border-blue-300 bg-white px-4 py-3 shadow-lg dark:border-blue-700 dark:bg-gray-900 max-w-[calc(100vw-2rem)]"
    >
      <RefreshCw className="h-4 w-4 shrink-0 text-blue-600 dark:text-blue-400" aria-hidden="true" />
      <span className="text-sm text-gray-800 dark:text-gray-100">
        {t('common.updateAvailable', 'A newer version of WeaveIn is available.')}
      </span>
      <button
        type="button"
        onClick={reload}
        className="shrink-0 rounded bg-blue-600 px-3 py-1 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
      >
        {t('common.refresh', 'Refresh')}
      </button>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label={t('common.dismiss', 'Dismiss')}
        className="shrink-0 rounded p-1 text-gray-500 hover:text-gray-800 dark:hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}
